import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, User, Calendar, Tag, Share2, BookOpen
} from 'lucide-react';
import landingService from '../../../services/landingService';
import ResourcesLayout from './ResourcesLayout';
import './BlogDetailPage.css';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(false);
      const result = await landingService.getBlogPost(slug);
      if (result.success && result.post) {
        setPost(result.post);
      } else {
        setError(true);
      }
      setLoading(false);
    };
    if (slug) fetchPost();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getCategoryLabel = (cat) => {
    const map = { product: 'Product', research: 'Research', community: 'Community', engineering: 'Engineering' };
    return map[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const map = { product: 'green', research: 'blue', community: 'purple', engineering: 'amber' };
    return map[cat] || 'green';
  };

  if (loading) {
    return (
      <ResourcesLayout>
        <div className="blogd-loading">
          <div className="blogd-loading-spinner" />
          <p>Loading article...</p>
        </div>
      </ResourcesLayout>
    );
  }

  if (error || !post) {
    return (
      <ResourcesLayout>
        <div className="blogd-error">
          <BookOpen size={48} />
          <h2>Article not found</h2>
          <p>The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="blogd-back-btn">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
      </ResourcesLayout>
    );
  }

  const initials = post.author?.name?.split(' ').map((n) => n[0]).join('') || 'RP';

  return (
    <ResourcesLayout>
      {/* ── Header ── */}
      <article className="blogd">
        <div className="blogd-header">
          <div className="blogd-header-inner">
            <Link to="/blog" className="blogd-breadcrumb">
              <ArrowLeft size={14} />
              Back to Blog
            </Link>

            <div className="blogd-meta-row">
              <span className={`blogd-category blogd-category--${getCategoryColor(post.category)}`}>
                {getCategoryLabel(post.category)}
              </span>
              <span className="blogd-meta-sep">·</span>
              <span className="blogd-date">
                <Calendar size={13} /> {formatDate(post.publishedAt)}
              </span>
              <span className="blogd-meta-sep">·</span>
              <span className="blogd-read-time">
                <Clock size={13} /> {post.readTime}
              </span>
            </div>

            <h1 className="blogd-title">{post.title}</h1>

            {post.description && (
              <p className="blogd-subtitle">{post.description}</p>
            )}

            <div className="blogd-author-row">
              <div className="blogd-author">
                <div className="blogd-author-avatar">{initials}</div>
                <div>
                  <span className="blogd-author-name">{post.author?.name}</span>
                  {post.author?.role && (
                    <span className="blogd-author-role">{post.author.role}</span>
                  )}
                </div>
              </div>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="blogd-tags">
                {post.tags.map((tag) => (
                  <span key={tag} className="blogd-tag">
                    <Tag size={11} /> {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        <div className="blogd-body">
          <div className="blogd-body-inner">
            {post.content ? (
              <div
                className="blogd-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            ) : (
              <div className="blogd-content">
                <p className="blogd-no-content">
                  Full article content is coming soon. Check back later for the complete read.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="blogd-footer">
          <div className="blogd-footer-inner">
            <Link to="/blog" className="blogd-back-link">
              <ArrowLeft size={15} /> All posts
            </Link>
            <button
              className="blogd-share-btn"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: post.title, url: window.location.href });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
            >
              <Share2 size={15} /> Share
            </button>
          </div>
        </div>
      </article>
    </ResourcesLayout>
  );
};

export default BlogDetailPage;
